import { CitedResponseSegments } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/CitedResponseSegments'
import { getAgenticConversationTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticConversation'
import { formatResultLabel } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticSearchText'
import type {
  AgenticConversationEvent,
  AgenticConversationResponseEvent,
  AgenticConversationTurn,
} from 'features/knowledgeDocumentViewer/types'
import React, { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

import { ResultCount, timelineItemEnter } from './agenticSearchChatStyles'

const markerPop = keyframes`
  0% {
    opacity: 0.35;
    transform: scale(0.55);
  }

  68% {
    opacity: 1;
    transform: scale(1.35);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const markerPulse = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in oklch, var(--foreground) 18%, transparent);
  }

  50% {
    box-shadow: 0 0 0 0.25rem transparent;
  }
`

const TimelineRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  align-items: start;
  min-height: 1.5rem;
  animation: ${timelineItemEnter} 180ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const MarkerCell = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
`

const MarkerDot = styled.span<{ $active?: boolean }>`
  width: 0.3125rem;
  height: 0.3125rem;
  border-radius: 9999rem;
  background: ${(props) =>
    props.$active ? 'var(--foreground)' : 'var(--muted-foreground)'};
  animation: ${markerPop} 180ms ease-out both;

  ${(props) =>
    props.$active &&
    css`
      animation:
        ${markerPop} 180ms ease-out both,
        ${markerPulse} 1.4s ease-out 180ms infinite;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const ConnectorRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  height: 0.625rem;
`

const StatusText = styled.p`
  min-width: 0;
  margin: 0;
  color: var(--muted-foreground);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SubmittedQueryText = styled.span`
  color: var(--primary);
`

const SubmittedQueryStatusText = styled(StatusText)`
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: unset;
`

const FinalResponseText = styled.div`
  min-width: 0;
  margin: 0;
  font-size: 1rem;
  line-height: 1.375rem;
  letter-spacing: 0;
`

const ResponseStatusText = styled(StatusText)`
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: unset;
`

const ThoughtText = styled(StatusText)`
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: unset;
`

function AnimatedThinkingText(): React.ReactElement {
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDots((prev) => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 400)
    return () => window.clearInterval(interval)
  }, [])

  return <StatusText aria-live='polite'>Thinking{dots}</StatusText>
}

function SearchedStatusText({
  searchLabel,
}: {
  searchLabel: string
}): React.ReactElement {
  return (
    <SubmittedQueryStatusText>
      Searched for <SubmittedQueryText>{searchLabel}</SubmittedQueryText>
    </SubmittedQueryStatusText>
  )
}

function ResponseText({
  event,
}: {
  event: AgenticConversationResponseEvent
}): React.ReactElement {
  const resultLabel = formatResultLabel(event.resultCount)
  return (
    <ResponseStatusText title={event.metadata.query_plan.elasticsearch_query}>
      Updated search query and found <ResultCount>{resultLabel}</ResultCount>
    </ResponseStatusText>
  )
}

function TimelineEventContent({
  event,
}: {
  event: AgenticConversationEvent
}): React.ReactElement {
  if (event.type === 'submitted_query') {
    return <SearchedStatusText searchLabel={event.query} />
  }
  if (event.type === 'thinking') {
    return <AnimatedThinkingText />
  }
  if (event.type === 'thought') {
    return <ThoughtText>{event.message}</ThoughtText>
  }
  if (event.type === 'response') {
    return <ResponseText event={event} />
  }
  return <StatusText>Search failed. Try again.</StatusText>
}

export function TimelineConnector(): React.ReactElement {
  return (
    <ConnectorRow>
      <div />
      <div />
    </ConnectorRow>
  )
}

function TimelineItem({
  active,
  children,
}: {
  active?: boolean
  children: React.ReactNode
}): React.ReactElement {
  return (
    <TimelineRow>
      <MarkerCell>
        <MarkerDot $active={active} />
      </MarkerCell>
      {children}
    </TimelineRow>
  )
}

function ResponseBodyRows({
  event,
}: {
  event: AgenticConversationResponseEvent
}): React.ReactElement | null {
  return (
    <CitedResponseSegments
      event={event}
      ResponseText={FinalResponseText}
      wrapItems={(items) =>
        items.map((item, index) => (
          <React.Fragment key={item.key ?? index}>
            <TimelineConnector />
            <TimelineItem>{item}</TimelineItem>
          </React.Fragment>
        ))
      }
    />
  )
}

/** Classic marker timeline for the header / landing conversation surface. */
export function AgenticTurnRows({
  turn,
}: {
  turn: AgenticConversationTurn
}): React.ReactElement {
  const isLoading = getAgenticConversationTurnStatus(turn) === 'loading'

  return (
    <>
      {turn.events.map((event, index) => (
        <React.Fragment key={event.id}>
          {index > 0 && <TimelineConnector />}
          <TimelineItem active={isLoading && index === turn.events.length - 1}>
            <TimelineEventContent event={event} />
          </TimelineItem>
          {event.type === 'response' && <ResponseBodyRows event={event} />}
        </React.Fragment>
      ))}
    </>
  )
}
